import React, { useState, useMemo, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { ProgressSpinner } from 'primereact/progressspinner';
import { ExternalUser } from '../../types/externalUser';
import { ComputedExerciseProgress } from '../../types/Fisiofit/exerciseHistory';
import { useClient } from '../../hooks/useClient';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import useSWR from 'swr';
import './ExerciseProgress.scss';
import { EXERCISE_TYPE } from '../../types/Fisiofit/exercise';
import { subMonths, format, subYears } from 'date-fns';
import { getRequestUrl } from '../../utils/urlUtils';
import { AdaptiveSelect } from '../AdaptiveSelect/AdaptiveSelect';
import { ExerciseViewIcon } from '../ExerciseViewIcon/ExerciseViewIcon';
import { ExerciseProgressMetric } from './ExerciseProgressMetric';
import { ExerciseProgressDetails } from './ExerciseProgressDetails';

interface DateRange {
  startDate: string;
  endDate: string;
}

const getUrl = (userId: string, dateRange?: DateRange) => {
  return getRequestUrl({
    url: `/external-users/${userId}/exercise-history`,
    stringParams: {
      startDate: dateRange?.startDate,
      endDate: dateRange?.endDate,
    },
  });
};

const datePeriodOptions = [
  { label: 'Último mes', value: '1m' },
  { label: 'Últimos 3 meses', value: '3m' },
  { label: 'Últimos 6 meses', value: '6m' },
  { label: 'Último año', value: '1y' },
  { label: 'Desde el inicio', value: 'all' },
];

function getDateRange(option: string): DateRange | undefined {
  const today = new Date();
  if (option === '1m') {
    return { startDate: format(subMonths(today, 1), 'yyyy-MM-dd'), endDate: format(today, 'yyyy-MM-dd') };
  }
  if (option === '3m') {
    return { startDate: format(subMonths(today, 3), 'yyyy-MM-dd'), endDate: format(today, 'yyyy-MM-dd') };
  }
  if (option === '6m') {
    return { startDate: format(subMonths(today, 6), 'yyyy-MM-dd'), endDate: format(today, 'yyyy-MM-dd') };
  }
  if (option === '1y') {
    return { startDate: format(subYears(today, 1), 'yyyy-MM-dd'), endDate: format(today, 'yyyy-MM-dd') };
  }
  return undefined;
}

const renderLoadingState = () => (
  <div className='loading-state'>
    <ProgressSpinner />
    <p>Cargando datos...</p>
  </div>
);

interface Props {
  user?: ExternalUser;
}

export const ExerciseProgress = ({ user }: Props) => {
  // Rejects on a failed request, so SWR sets `error` and the error message shows instead of the empty one (#83)
  const { get } = useClient({ throwOnError: true });
  const [visible, setVisible] = useState(false);
  const wasOpened = useRef(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [openExerciseIds, setOpenExerciseIds] = useState<string[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('3m');

  const dateRange = useMemo(() => getDateRange(selectedPeriod), [selectedPeriod]);

  const {
    data: exercises,
    isLoading,
    error,
  } = useSWR((visible || wasOpened.current) && user?.id ? getUrl(user.id, dateRange) : null, get<ComputedExerciseProgress[]>);

  const filteredExercises = useMemo(() => {
    if (!exercises) return [];
    if (!searchQuery) return exercises;
    return exercises.filter((exercise) => exercise.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [exercises, searchQuery]);

  const activeIndexes = useMemo(() => {
    return openExerciseIds
      .map((id) => filteredExercises.findIndex((exercise) => exercise.exerciseId === id))
      .filter((index) => index !== -1);
  }, [filteredExercises, openExerciseIds]);

  const handleAccordionChange = (indexes: number[]) => {
    const newOpenExerciseIds = indexes
      .map((index) => filteredExercises[index]?.exerciseId)
      .filter((id): id is string => id !== undefined);
    setOpenExerciseIds(newOpenExerciseIds);
  };

  const handleOpenModal = () => {
    setVisible(true);
    wasOpened.current = true;
  };

  const renderExerciseHeader = (exercise: ComputedExerciseProgress) => {
    return (
      <div className='exercise-header'>
        <div className='exercise-header-left'>
          <ExerciseViewIcon exercise={exercise} />
          <h3 className='exercise-name'>{exercise.name}</h3>
        </div>
        <div className='exercise-metrics'>
          <ExerciseProgressMetric title='Peso' value={exercise.currentWeight} diff={exercise.weightDiff} unit='kg' />
          {exercise.exerciseType === EXERCISE_TYPE.REPS && (
            <ExerciseProgressMetric title='Repeticiones' value={exercise.currentRepetitions} diff={exercise.repetitionsDiff} />
          )}
          {exercise.exerciseType === EXERCISE_TYPE.TIME && (
            <ExerciseProgressMetric title='Tiempo' value={exercise.currentDuration} diff={exercise.durationDiff} unit='s' />
          )}
          <ExerciseProgressMetric title='RIR' value={exercise.currentRir} diff={exercise.rirDiff} isRir />
          <ExerciseProgressMetric title='RPE' value={exercise.currentRpe} diff={exercise.rpeDiff} isRpe />
        </div>
      </div>
    );
  };

  return (
    <>
      <Button outlined label='Ver progreso' icon='pi pi-chart-line' onClick={handleOpenModal} />
      <Dialog
        header={`Progreso de ${user?.name || ''} ${user?.surnames || ''}`}
        visible={visible}
        onHide={() => {
          setVisible(false);
          setSearchQuery('');
          setOpenExerciseIds([]);
        }}
        className='exercise-progress-dialog big-modal'
        modal
        draggable={false}
        resizable={false}
      >
        <div className='exercise-progress-content'>
          <div className='exercise-progress-filters'>
            <IconField className='exercise-search'>
              <InputText value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder='Buscar ejercicio' />
              <InputIcon className='pi pi-search' />
            </IconField>
            <AdaptiveSelect
              value={selectedPeriod}
              options={datePeriodOptions}
              onChange={(e) => setSelectedPeriod(e)}
              className='date-range-selector'
              modalTitle='Seleccionar periodo'
            />
          </div>

          <div className='exercises-list'>
            {isLoading ? (
              renderLoadingState()
            ) : error ? (
              <div className='no-exercises'>
                <i className='pi pi-times-circle' />
                <p>Error al cargar el progreso de ejercicios</p>
              </div>
            ) : filteredExercises.length === 0 ? (
              <div className='no-exercises'>
                <i className='pi pi-info-circle' />
                <p>{searchQuery ? 'No se encontraron ejercicios' : 'No hay ejercicios con progreso registrado'}</p>
              </div>
            ) : (
              <Accordion
                multiple
                activeIndex={activeIndexes}
                onTabChange={(e) => handleAccordionChange(e.index as number[])}
                className='exercise-accordion'
              >
                {filteredExercises.map((exercise) => (
                  <AccordionTab key={exercise.exerciseId} header={renderExerciseHeader(exercise)}>
                    <ExerciseProgressDetails exercise={exercise} />
                  </AccordionTab>
                ))}
              </Accordion>
            )}
          </div>
        </div>
      </Dialog>
    </>
  );
};
